/**
 * Configuración e interceptores de Axios para manejar peticiones HTTP
 * Este archivo configura los interceptores para manejar automáticamente
 * la autenticación, renovación de tokens y errores comunes.
 */
import axios from 'axios';
import store from '@/store';
import router from '@/router';

// Crear instancia base de axios
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 segundos de timeout
});

// Interceptor para añadir el token a las peticiones
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Si el error es 401 (No autorizado) y no hemos intentado renovar el token
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Aquí se implementaría la lógica para renovar el token si es necesario
        // Por ahora, simplemente cerramos sesión si el token no es válido
        await store.dispatch('user/logout');
        
        // Verificar si ya estamos en la página de login antes de navegar
        if (router.currentRoute.name !== 'login') {
          router.push({ name: 'login' });
        }
        
        return Promise.reject(new Error('Sesión expirada. Por favor, inicie sesión nuevamente.'));
      } catch (refreshError) {
        // Si no se puede renovar el token, cerrar sesión
        await store.dispatch('user/logout');
        
        // Verificar si ya estamos en la página de login antes de navegar
        if (router.currentRoute.name !== 'login') {
          router.push({ name: 'login' });
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // Manejar otros errores comunes
    if (error.response) {
      switch (error.response.status) {
        case 400:
          error.message = 'Solicitud incorrecta';
          break;
        case 403:
          error.message = 'Acceso denegado';
          break;
        case 404:
          error.message = 'Recurso no encontrado';
          break;
        case 500:
          error.message = 'Error interno del servidor';
          break;
        default:
          error.message = `Error: ${error.response.status}`;
      }
    } else if (error.request) {
      error.message = 'No se recibió respuesta del servidor';
    } else {
      error.message = 'Error al realizar la solicitud';
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
